import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnimatedDemo.css';

// Animated "you do not have to keep checking" walkthrough. Code-driven, no video.
//
// Grade Watch is the only feature in the tour that answers "why would I come
// back", so the demo is built around the passage of time: set the watch, let
// days pass, and the email arrives on its own. The shift shown is a downgrade
// on purpose. An alert that only ever brings good news is not worth trusting,
// and the honest version is the one that tells you when something you own got
// worse.

export default function GradeWatchDemo({ autoPlay = true, loop = true }) {
  // 0=graded row, 1=bell clicked, 2=watching, 3=days pass, 4=alert arrives
  const [phase, setPhase] = useState(0);
  const [cursor, setCursor] = useState({ x: 50, y: 30, visible: false, clicking: false });
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
    timers.current = [];
  };

  const runDemo = useCallback(() => {
    clearTimers();
    setPhase(0);
    setCursor({ x: 50, y: 30, visible: false, clicking: false });

    // Move to the bell and press it.
    timers.current.push(setTimeout(() => setCursor({ x: 243, y: 62, visible: true, clicking: false }), 900));
    timers.current.push(setTimeout(() => setCursor({ x: 243, y: 62, visible: true, clicking: true }), 1500));
    timers.current.push(setTimeout(() => {
      setCursor((p) => ({ ...p, visible: false }));
      setPhase(1);
    }, 1800));

    timers.current.push(setTimeout(() => setPhase(2), 2200));
    timers.current.push(setTimeout(() => setPhase(3), 3800));
    timers.current.push(setTimeout(() => setPhase(4), 5200));
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return () => clearTimers();
    const loopId = setInterval(runDemo, 12000);
    return () => { clearInterval(loopId); clearTimers(); };
  }, [autoPlay, loop, runDemo]);

  const watching = phase >= 1;

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/grade/NVDA</span>
      </div>
      <div className="demo-body" style={{ position: 'relative' }}>
        {cursor.visible && (
          <svg
            className="demo-cursor-arrow"
            style={{
              left: cursor.x, top: cursor.y,
              transform: cursor.clicking ? 'scale(0.85)' : 'scale(1)',
            }}
            viewBox="0 0 24 24" fill="white" stroke="black" strokeWidth="1"
          >
            <path d="M5 3l14 8-6 2-4 6z" />
          </svg>
        )}

        {/* The graded row, with the bell that starts the watch. */}
        <div style={{
          background: 'var(--bg-primary, #0C1220)',
          border: '1px solid var(--border-subtle, #1E293B)',
          borderRadius: 10, padding: '0.75rem 0.8rem',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono, monospace)', fontWeight: 800,
            fontSize: '1rem', color: 'var(--neon-green, #00D4AA)',
          }}>NVDA</span>

          <span style={{
            fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem',
            fontWeight: 700, color: '#6495ED',
            padding: '0.15rem 0.5rem', background: 'rgba(100,149,237,0.12)',
            borderRadius: 4,
          }}>Conv: 4.1</span>

          <span style={{ flex: 1 }} />

          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: '0.68rem', fontWeight: 700,
            padding: '0.28rem 0.55rem', borderRadius: 6,
            border: `1px solid ${watching ? 'rgba(0,212,170,0.5)' : 'var(--border-medium, #334155)'}`,
            background: watching ? 'rgba(0,212,170,0.12)' : 'transparent',
            color: watching ? 'var(--neon-green, #00D4AA)' : 'var(--text-secondary, #94A3B8)',
            transition: 'all 0.3s ease',
          }}>
            <span aria-hidden="true">{watching ? '🔔' : '🔕'}</span>
            {watching ? 'Watching' : 'Watch grade'}
          </span>
        </div>

        {phase === 2 && (
          <div style={{
            marginTop: '0.6rem', fontSize: '0.72rem',
            color: 'var(--text-secondary, #94A3B8)',
            animation: 'fadeIn 0.3s ease both',
          }}>
            We will email you if the grade moves. No app to check.
          </div>
        )}

        {phase >= 3 && (
          <div style={{
            marginTop: '0.7rem', marginBottom: '0.5rem',
            display: 'flex', alignItems: 'center', gap: 8,
            animation: 'fadeIn 0.3s ease both',
          }}>
            <span style={{ flex: 1, height: 1, background: 'var(--border-subtle, #1E293B)' }} />
            <span style={{
              fontSize: '0.62rem', color: 'var(--text-muted, #64748B)',
              textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
            }}>Three days later</span>
            <span style={{ flex: 1, height: 1, background: 'var(--border-subtle, #1E293B)' }} />
          </div>
        )}

        {/* The alert itself, shaped like the email that actually arrives. */}
        {phase >= 4 && (
          <div style={{
            background: 'var(--bg-primary, #0C1220)',
            border: '1px solid rgba(251,191,36,0.4)',
            borderRadius: 10, padding: '0.75rem 0.8rem',
            animation: 'slideInUp 0.4s ease both',
          }}>
            <div style={{
              fontSize: '0.6rem', color: 'var(--text-muted, #64748B)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              fontWeight: 700, marginBottom: '0.35rem',
            }}>
              📧 Email from AlphaGrade
            </div>

            <div style={{
              fontSize: '0.82rem', fontWeight: 700,
              color: 'var(--text-primary, #E2E8F0)', marginBottom: '0.4rem',
            }}>
              NVDA moved from Favorable to Neutral
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: 'var(--font-mono, monospace)', fontSize: '0.76rem',
              fontWeight: 700, marginBottom: '0.45rem',
            }}>
              <span style={{ color: '#6495ED' }}>4.1</span>
              <span style={{ color: 'var(--text-muted, #64748B)' }}>→</span>
              <span style={{ color: '#FBBF24' }}>3.4</span>
            </div>

            <div style={{
              fontSize: '0.68rem', color: 'var(--text-secondary, #94A3B8)',
              lineHeight: 1.5,
            }}>
              Setup weakened. Momentum rolled over and the entry is no longer in
              range. Quality is unchanged, so this is about timing, not the
              business.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
